import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../helpers/apiUtils';
import InfoTip from '../../components/InfoTip';

/**
 * Webinarverteiler (Plan .claude/plans/2026-09-30-yannick-paket-webinarverteiler-dnv.md § P4): eigene Liste,
 * Versand per SES von webinare.finanztrends.de. Grösse, Zuwachs je Quelle, Abmeldungen, Performance je Sendung,
 * Vorher/Nachher derselben Personen.
 */

const API = '/api/webinar-verteiler';

interface Sendung {
  id: number;
  kind: 'heatup' | 'followup' | 'rerun_followup';
  source_ref: string;
  webinar_id: string | null;
  title: string | null;
  slot: string;
  status: string;
  skip_reason: string | null;
  error_message: string | null;
  subject: string | null;
  gesendet: number;
  fehler: number;
  uebersprungen: number;
  zugestellt: number;
  geoeffnet: number;
  geklickt: number;
  bounce_beschwerde: number;
  anmeldungen: number;
  kaeufe: number;
}

interface Overview {
  config: { enabled: boolean; sendEnabled: boolean; fromEmail: string; configurationSet: string };
  mitglieder: {
    gesamt: number;
    aktiv: number;
    wartend: number;
    abgemeldet: number;
    bounced: number;
    beschwerden: number;
    entfernt: number;
    jeStatusBasis: { status: string; basis: string; n: number }[];
  };
  zuwachs: { woche: string; quelle: string; n: number }[];
  sendungen: Sendung[];
  vorherNachher: { personen: number; vorher: number; nachher: number };
}

const KIND_LABEL: Record<Sendung['kind'], string> = {
  heatup: 'Heat-up',
  followup: 'Nachfass',
  rerun_followup: 'Wiederholung-Nachfass',
};

const fmt = (n: number) => n.toLocaleString('de-DE');
const pro1000 = (n: number, basis: number) => (basis > 0 ? ((n / basis) * 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) : '–');
const pct = (n: number, basis: number) => (basis > 0 ? `${((n / basis) * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %` : '–');

function Kachel({ label, wert, hinweis }: { label: string; wert: string; hinweis?: string }) {
  return (
    <Paper sx={{ p: 2, minWidth: 150, flex: '1 1 150px' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
        {hinweis ? <InfoTip text={hinweis} /> : null}
      </Typography>
      <Typography variant="h4">{wert}</Typography>
    </Paper>
  );
}

const WebinarVerteiler: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await fetchData(`${API}/overview`);
    if (r?.success) setData(r.data as Overview);
    else setError(r?.error || 'Laden fehlgeschlagen');
    setLoading(false);
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  const wochen = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    const quellen = new Set<string>();
    for (const z of data?.zuwachs ?? []) {
      quellen.add(z.quelle);
      m.set(z.woche, { ...(m.get(z.woche) ?? {}), [z.quelle]: z.n });
    }
    return { zeilen: [...m.entries()].sort(([a], [b]) => (a < b ? 1 : -1)), quellen: [...quellen].sort() };
  }, [data]);

  const m = data?.mitglieder;
  const abmeldeBasis = m ? m.gesamt - m.wartend : 0;

  return (
    <Box m="20px">
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="h3">Webinarverteiler</Typography>
        {data && (
          <>
            <Chip size="small" color={data.config.enabled ? 'success' : 'default'} label={data.config.enabled ? 'Sperren scharf' : 'Sperren aus'} />
            <Chip size="small" color={data.config.sendEnabled ? 'success' : 'default'} label={data.config.sendEnabled ? 'Versand an' : 'Versand aus'} />
            <Typography variant="body2" color="text.secondary">
              {data.config.fromEmail} · {data.config.configurationSet}
            </Typography>
          </>
        )}
        <Box flex={1} />
        <Button startIcon={<RefreshIcon />} onClick={laden} disabled={loading}>
          Aktualisieren
        </Button>
      </Stack>

      {loading && !data && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {m && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={2} mb={3}>
            <Kachel label="Aktiv" wert={fmt(m.aktiv)} hinweis="Bekommen Heat-ups und Nachfass per SES. Aktiv erst, wenn FT-Sperrliste und Partner-Segmente nach dem Beitritt aufgefrischt sind." />
            <Kachel label="Wartend" wert={fmt(m.wartend)} hinweis="Schon aus FT- und Partner-Heat-ups genommen, noch kein Verteiler-Versand." />
            <Kachel label="Abgemeldet" wert={`${fmt(m.abgemeldet)} (${pct(m.abgemeldet, abmeldeBasis)})`} hinweis="Vorerst von aller Webinar-Werbung ausgeschlossen." />
            <Kachel label="Bounces" wert={`${fmt(m.bounced)} (${pct(m.bounced, abmeldeBasis)})`} />
            <Kachel label="Beschwerden" wert={`${fmt(m.beschwerden)} (${pct(m.beschwerden, abmeldeBasis)})`} hinweis="Zaehlt wie eine Abmeldung. Ziel: deutlich unter 0,1 %." />
            <Kachel label="FT-Abmeldung → entfernt" wert={fmt(m.entfernt)} />
          </Stack>

          <Typography variant="h5" mb={1}>
            Sendungen
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Slot</TableCell>
                  <TableCell>Art</TableCell>
                  <TableCell>Webinar</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Gesendet</TableCell>
                  <TableCell align="right">Zugestellt</TableCell>
                  <TableCell align="right">Öffner ‰</TableCell>
                  <TableCell align="right">Klicker ‰</TableCell>
                  <TableCell align="right">Anmeldungen ‰</TableCell>
                  <TableCell align="right">Käufe ‰</TableCell>
                  <TableCell align="right">Bounce/Beschw.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data!.sendungen.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>Noch keine Sendungen.</TableCell>
                  </TableRow>
                )}
                {data!.sendungen.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.slot}</TableCell>
                    <TableCell>{KIND_LABEL[s.kind] ?? s.kind}</TableCell>
                    <TableCell>{s.title || s.webinar_id || '–'}</TableCell>
                    <TableCell>
                      {s.status}
                      {s.skip_reason ? ` (${s.skip_reason})` : ''}
                      {s.error_message ? <InfoTip text={s.error_message} /> : null}
                    </TableCell>
                    <TableCell align="right">
                      {fmt(s.gesendet)}
                      {s.fehler ? ` / ${fmt(s.fehler)} Fehler` : ''}
                    </TableCell>
                    <TableCell align="right">{fmt(s.zugestellt)}</TableCell>
                    <TableCell align="right">{pro1000(s.geoeffnet, s.gesendet)}</TableCell>
                    <TableCell align="right">{pro1000(s.geklickt, s.gesendet)}</TableCell>
                    <TableCell align="right">
                      {pro1000(s.anmeldungen, s.gesendet)} <small>({s.anmeldungen})</small>
                    </TableCell>
                    <TableCell align="right">
                      {pro1000(s.kaeufe, s.gesendet)} <small>({s.kaeufe})</small>
                    </TableCell>
                    <TableCell align="right">{fmt(s.bounce_beschwerde)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
            <Box flex={1}>
              <Typography variant="h5" mb={1}>
                Zuwachs je Woche und Quelle
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Woche ab</TableCell>
                      {wochen.quellen.map((q) => (
                        <TableCell key={q} align="right">
                          {q}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wochen.zeilen.map(([woche, n]) => (
                      <TableRow key={woche}>
                        <TableCell>{woche}</TableCell>
                        {wochen.quellen.map((q) => (
                          <TableCell key={q} align="right">
                            {n[q] ? fmt(n[q]) : '–'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box flex={1}>
              <Typography variant="h5" mb={1}>
                Vorher/Nachher derselben Personen
                <InfoTip text="Webinar-Anmeldungen je 1.000 Mitglieder: 60 Tage vor dem Beitritt vs. 60 Tage danach. Nur Mitglieder, die seit mindestens 60 Tagen dabei sind. Der Altbestand trat mit seiner bisherigen Historie bei — der Vorher-Wert enthaelt deshalb die Anmeldung, die ihn zum Mitglied gemacht hat." />
              </Typography>
              <Paper sx={{ p: 2 }}>
                {data!.vorherNachher.personen === 0 ? (
                  <Typography>Noch keine Mitglieder mit 60 Tagen Laufzeit.</Typography>
                ) : (
                  <Typography>
                    {fmt(data!.vorherNachher.personen)} Personen: vorher {pro1000(data!.vorherNachher.vorher, data!.vorherNachher.personen)} ‰, nachher{' '}
                    {pro1000(data!.vorherNachher.nachher, data!.vorherNachher.personen)} ‰
                  </Typography>
                )}
              </Paper>
            </Box>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default WebinarVerteiler;
